/**
 * Product Upsell Handler
 * Handles adding multiple products to cart when upsell checkbox is ticked
 */
class ProductUpsellHandler extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
  }

  connectedCallback() {
    this.abortController = new AbortController();
    this.productForm = this.closest('form[is="product-form"]');

    if (!this.productForm) {
      console.error('ProductUpsellHandler: Could not find parent product form');
      return;
    }

    // Listen to form submit to intercept and add upsell if checked
    this.productForm.addEventListener('submit', this.handleFormSubmit.bind(this), {
      signal: this.abortController.signal,
      capture: true // Use capture phase to intercept before product-form processes it
    });
  }

  disconnectedCallback() {
    if (this.abortController) {
      this.abortController.abort();
    }
  }

  /**
   * Handle form submission and add upsell product if checkbox is checked
   */
  async handleFormSubmit(event) {
    const upsellCheckbox = this.querySelector('[data-upsell-checkbox]');

    // If no checkbox or checkbox is not checked, let the form submit normally
    if (!upsellCheckbox || !upsellCheckbox.checked) {
      return;
    }

    // Checkbox is checked - we need to add both products
    event.preventDefault();
    event.stopPropagation();

    const mainVariantId = this.productForm.querySelector('[name="id"]').value;
    const upsellVariantId = upsellCheckbox.value;
    const quantityInput = this.productForm.querySelector('[name="quantity"]');
    const quantity = quantityInput ? parseInt(quantityInput.value) : 1;

    // Collect any line item properties from the main form
    const formData = new FormData(this.productForm);
    const properties = {};
    for (const [key, value] of formData.entries()) {
      if (key.startsWith('properties[')) {
        const propName = key.match(/properties\[(.*?)\]/)[1];
        properties[propName] = value;
      }
    }

    // Set loading state on submit buttons
    const submitButtons = this.productForm.querySelectorAll('[type="submit"]');
    submitButtons.forEach(button => {
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
    });

    try {
      // Prepare sections to bundle - starts with variant-added
      // Other components can add their sections by listening to this event
      let sectionsToBundle = ['variant-added'];
      const prepareEvent = new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      });
      document.documentElement.dispatchEvent(prepareEvent);

      // Add both products to cart using Shopify Cart API
      const addResponse = await this.addMultipleItemsToCart([
        {
          id: mainVariantId,
          quantity: quantity,
          properties: properties
        },
        {
          id: upsellVariantId,
          quantity: 1
        }
      ]);

      // Fetch updated cart
      const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
      const cartContent = await cartResponse.json();

      // Fetch the sections using Section Rendering API
      const sectionsParam = sectionsToBundle.join(',');
      const sectionsResponse = await fetch(
        `${window.Shopify.routes.root}?sections=${sectionsParam}`,
        {
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        }
      );
      const sectionsData = await sectionsResponse.json();

      // Add sections to cart content in the format the theme expects
      cartContent.sections = sectionsData;

      // Get the items we just added (from the add response)
      const addedItems = Array.isArray(addResponse) ? addResponse : [addResponse];

      // Dispatch success events to trigger cart drawer
      this.productForm.dispatchEvent(new CustomEvent('variant:add', {
        bubbles: true,
        detail: {
          items: addedItems,
          cart: cartContent
        }
      }));

      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'variant:add',
          cart: cartContent
        }
      }));

      // Check if theme redirects to cart page
      if (window.themeVariables?.settings?.cartType === 'page' ||
          window.themeVariables?.settings?.pageType === 'cart') {
        window.location.href = `${window.Shopify.routes.root}cart`;
      }

    } catch (error) {
      console.error('Error adding upsell products to cart:', error);

      // Dispatch error event
      this.productForm.dispatchEvent(new CustomEvent('cart:error', {
        bubbles: true,
        detail: {
          error: error.message || 'Unable to add products to cart. Please try again.'
        }
      }));

      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } finally {
      // Remove loading state
      submitButtons.forEach(button => {
        button.removeAttribute('aria-busy');
        button.disabled = false;
      });
    }
  }

  /**
   * Add multiple items to cart using Shopify Cart API
   */
  async addMultipleItemsToCart(items) {
    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || errorData.message || 'Failed to add items to cart');
    }

    return response.json();
  }
}

// Register the custom element
if (!window.customElements.get('product-upsell-handler')) {
  window.customElements.define('product-upsell-handler', ProductUpsellHandler);
}
